import React, { useState } from 'react';
import { Button, Dialog, Notice } from '@origo/origo-ui';
import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { ApiError } from '../../api/client';
import { useStartInitialLoad } from '../../api/hooks';
import styles from '../common/panel.module.css';

interface InitialLoadProps {
    bindingId: string;
    displayName: string;
}

/**
 * Bestiller full initial last AGOL → Sentral. Bevisst en egen handling: en ny kobling synker bare
 * endringer etter opprettelse, og noen ganger finnes dataene allerede i det sentrale datasettet.
 */
export const InitialLoad: React.FC<InitialLoadProps> = ({ bindingId, displayName }) => {
    const mutation = useStartInitialLoad();
    const [confirm, setConfirm] = useState(false);

    const start = async () => {
        try {
            await mutation.mutateAsync(bindingId);
            setConfirm(false);
        } catch {
            /* feil vises under */
        }
    };

    const errorMessage = mutation.error instanceof ApiError
        ? mutation.error.message
        : mutation.error
            ? 'Kunne ikke bestille initial last.'
            : null;

    return (
        <>
            <div className={styles.rowActions}>
                <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<DownloadSimpleIcon size={16} weight="bold" />}
                    onClick={() => {
                        mutation.reset();
                        setConfirm(true);
                    }}
                    disabled={mutation.isPending}
                >
                    Hent alle objekter fra AGOL
                </Button>
            </div>

            {mutation.isSuccess && !confirm && (
                <Notice variant="success">Initial last bestilt. Worker henter objektene side for side.</Notice>
            )}

            <Dialog
                open={confirm}
                onClose={() => setConfirm(false)}
                title="Hent alle objekter fra AGOL?"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setConfirm(false)}>
                            Avbryt
                        </Button>
                        <Button variant="primary" onClick={start} disabled={mutation.isPending}>
                            {mutation.isPending ? 'Bestiller…' : 'Start initial last'}
                        </Button>
                    </>
                }
            >
                <p>
                    Alle objekter i de mappede lagene for <strong>{displayName}</strong> sendes til sentralt
                    datasett. Objekter som finnes der fra før, blir oppdatert — det blir ikke duplikater.
                </p>
                <p>
                    Store datasett kan ta tid. Fremdriften vises i loggen, og lasten fortsetter der den slapp
                    hvis noe feiler underveis.
                </p>
                {errorMessage && <Notice variant="danger">{errorMessage}</Notice>}
            </Dialog>
        </>
    );
};
